import { AlertTriangleIcon, InfoIcon, Loader2, MailIcon } from "lucide-react"
import { useEffect, useId, useState } from "react"

import { type OAuthError, oauthStartHref } from "@/auth/oauth"
import { useUi } from "@/core/i18n/locale-context"
import { Alert, AlertDescription } from "@/ui/alert"
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"

import { AppleLogo, GoogleLogo } from "./provider-icons"

interface SignInProps {
  /** Present when Google and Apple sign-in are switched on (VITE_SIGNIN_OAUTH_ENABLED); `redirectTo` is the sanitised post-login target. */
  oauth?: { redirectTo: string }
  /** Why a Google or Apple sign-in ended back on this page (from `/login?oauthError=`). */
  oauthError?: OAuthError
  email?: string
  error?: string
  success?: string
  isPending?: boolean
  /** Returns from the code step to the email step */
  onUseDifferentEmail?: () => void
  /** Sends a new code to the current email (submits the form without the code) */
  onResend?: (form: HTMLFormElement) => void
}

const OTP_LENGTH = 6

const INVALID_CODE_MESSAGE = "Invalid verification code"

// Official button styles: Google "light"/"dark" themes (stroke, fill, text colours) and Apple's black/white buttons,
// both full width and the same height so Apple's is not smaller than the other (HIG).
const PROVIDER_BUTTON = "h-11 w-full gap-3 text-sm"
const GOOGLE_BUTTON =
  "border-[#747775] bg-white text-[#1f1f1f] hover:bg-[#f2f2f2] hover:text-[#1f1f1f] dark:border-[#8e918f] dark:bg-[#131314] dark:text-[#e3e3e3] dark:hover:bg-[#1e1f20] dark:hover:text-[#e3e3e3]"
const APPLE_BUTTON =
  "border-black bg-black text-white hover:bg-black/85 hover:text-white dark:border-white dark:bg-white dark:text-black dark:hover:bg-white/85 dark:hover:text-black"

export const SignIn = ({
  oauth,
  oauthError,
  email,
  error,
  success,
  isPending,
  onUseDifferentEmail,
  onResend,
}: SignInProps) => {
  const [otp, setOtp] = useState("")
  const emailInputId = useId()
  const otpInputId = useId()
  const messageId = useId()
  const providers = useUi().signInProviders
  const oauthMessage = oauthError
    ? {
        cancelled: providers.errors.cancelled,
        failed: providers.errors.failed,
        email_exists: providers.errors.emailExists,
      }[oauthError]
    : undefined

  // oxlint-disable-next-line react/exhaustive-deps -- executing effect on error, email change is expected
  useEffect(() => {
    setOtp("")
  }, [error, email])

  return (
    <div className="bg-card w-full max-w-md space-y-5 rounded-xl border p-6 shadow-sm sm:p-9">
      <div className="grid gap-1.5">
        <h1 className="text-2xl font-semibold tracking-tight">
          {email ? "Check your email" : "Sign in or create your account"}
        </h1>
        <p className="text-muted-foreground text-sm">
          {email
            ? `We sent a 6-digit code to ${email}. It's valid for 10 minutes.`
            : "Enter your email and we'll send a 6-digit code. No password to remember."}
        </p>
      </div>

      {email && <input type="hidden" name="email" value={email} />}

      {error && (
        <Alert variant="destructive" id={messageId}>
          <AlertTriangleIcon />
          <AlertDescription>
            {email && error === INVALID_CODE_MESSAGE ? (
              <>
                <b className="block font-semibold">That code isn't right</b>
                Check the latest email and try again. Codes expire after 10 minutes.
              </>
            ) : (
              error
            )}
          </AlertDescription>
        </Alert>
      )}

      {oauthMessage && !error && !email && (
        <Alert variant="destructive">
          <AlertTriangleIcon />
          <AlertDescription>{oauthMessage}</AlertDescription>
        </Alert>
      )}

      {success && !error && !email && (
        <Alert id={messageId}>
          <InfoIcon />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {!email && oauth && (
        <>
          {/* Plain links: the server route sets the PKCE cookie and redirects, so this works without JavaScript. */}
          <div role="group" aria-label={providers.groupLabel} className="grid gap-3">
            <Button
              asChild
              variant="outline"
              size="lg"
              className={`${PROVIDER_BUTTON} ${GOOGLE_BUTTON}`}
            >
              <a href={oauthStartHref("google", oauth.redirectTo)}>
                <GoogleLogo className="size-[18px]" />
                {providers.continueWithGoogle}
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className={`${PROVIDER_BUTTON} ${APPLE_BUTTON}`}
            >
              <a href={oauthStartHref("apple", oauth.redirectTo)}>
                <AppleLogo className="size-[18px]" />
                {providers.continueWithApple}
              </a>
            </Button>
          </div>
          <div
            aria-hidden="true"
            className="text-muted-foreground flex items-center gap-3 text-xs uppercase"
          >
            <span className="bg-border h-px flex-1" />
            {providers.or}
            <span className="bg-border h-px flex-1" />
          </div>
        </>
      )}

      {!email ? (
        <div className="grid gap-2">
          <Label htmlFor={emailInputId}>Email</Label>
          <div className="relative">
            <MailIcon
              aria-hidden="true"
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
            />
            <Input
              type="email"
              id={emailInputId}
              name="email"
              autoComplete="email"
              placeholder="Enter your email"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? messageId : undefined}
              className="h-11 pl-9 md:h-10"
            />
          </div>
        </div>
      ) : (
        <div className="grid gap-2">
          <Label htmlFor={otpInputId}>Verification code</Label>
          <Input
            type="text"
            id={otpInputId}
            name="otp"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={OTP_LENGTH}
            placeholder="Verification code"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? messageId : undefined}
            className="h-14 text-center text-2xl font-semibold tracking-[0.4em] tabular-nums placeholder:text-base placeholder:font-normal placeholder:tracking-normal md:text-2xl"
          />
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        className="h-11 w-full"
        disabled={isPending || (!!email && otp.length < OTP_LENGTH)}
      >
        {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
        {email ? "Verify and continue" : "Email me a code"}
      </Button>

      {email ? (
        <div className="grid gap-1">
          <div className="text-muted-foreground flex min-h-11 items-center justify-between text-sm min-[769px]:min-h-6">
            <span>Code expired or lost?</span>
            <button
              type="button"
              disabled={isPending}
              onClick={(e) => e.currentTarget.form && onResend?.(e.currentTarget.form)}
              className="text-primary min-h-11 font-semibold underline-offset-4 hover:underline min-[769px]:min-h-6"
            >
              Send a new code
            </button>
          </div>
          <button
            type="button"
            onClick={onUseDifferentEmail}
            className="text-primary mx-auto flex min-h-11 items-center text-sm font-medium underline-offset-4 hover:underline min-[769px]:min-h-6"
          >
            Use a different email
          </button>
        </div>
      ) : (
        <p className="text-muted-foreground text-center text-xs">
          By continuing you agree to the{" "}
          <a href="/legal/terms-and-conditions" className="underline">
            Terms
          </a>{" "}
          and{" "}
          <a href="/legal/privacy" className="underline">
            Privacy Policy
          </a>
          .
        </p>
      )}
    </div>
  )
}
